/**
 * 极简紫韵模版 - 注册页
 * 
 * 设计理念：顶部进度指示器 + 单页紧凑表单，动态字段由后台配置
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";
import request, { getSinglePage } from "@/utils/api";

// 注册字段类型
type FieldType = "text" | "textarea" | "select" | "radio" | "checkbox" | "password" | "number" | "date" | "file" | "image";

interface RegisterField {
  field_name: string;
  field_label: string;
  field_type: FieldType;
  placeholder: string;
  hint?: string;
  options?: string[];
  is_required: boolean;
  is_enabled: boolean;
  sort_order: number;
}

// 获取注册配置
async function getRegisterConfig(siteId: string): Promise<{ fields: RegisterField[]; sms_enabled?: boolean }> {
  return request<{ fields: RegisterField[]; sms_enabled?: boolean }>(`/register/config`, {
    params: { site_id: siteId },
  });
}

// 发送短信验证码
async function sendSmsCode(siteId: string, phone: string): Promise<void> {
  return request("/register/send-code", {
    method: "POST",
    params: { site_id: siteId },
    body: { phone },
  });
}

// 文件上传
async function uploadFile(file: File): Promise<{ url: string }> {
  const formData = new FormData();
  formData.append("file", file);
  return request("/upload", {
    method: "POST",
    body: formData,
  });
}

export default function PurpleRegisterPage() {
  const navigate = useNavigate();
  const { siteId } = useSite();
  const { siteConfig } = useSiteConfig();

  const [fields, setFields] = useState<RegisterField[]>([]);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [uploadingFields, setUploadingFields] = useState<Record<string, boolean>>({});
  const formDataRef = useRef<Record<string, string>>({});

  // 短信验证
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [smsCountdown, setSmsCountdown] = useState(0);
  const [smsLoading, setSmsLoading] = useState(false);

  // 协议弹窗
  const [agreementModal, setAgreementModal] = useState<{ show: boolean; title: string; content: string; loading: boolean }>({
    show: false, title: "", content: "", loading: false,
  });

  const siteName = siteConfig?.site_name || siteConfig?.name || "";
  const logo = siteConfig?.logo || "";

  // 加载注册配置
  useEffect(() => {
    async function loadConfig() {
      try {
        const config = await getRegisterConfig(siteId);
        const enabledFields = config.fields.filter((f) => f.is_enabled);

        const initialData: Record<string, string> = {};
        enabledFields.forEach((field) => {
          initialData[field.field_name] = "";
        });

        if (config.sms_enabled) {
          initialData['phone'] = '';
          initialData['sms_code'] = '';
          setSmsEnabled(true);
        }

        setFields(enabledFields);
        setFormData(initialData);
        formDataRef.current = initialData;
      } catch (err) {
        console.error("加载注册配置失败:", err);
        setError("加载表单配置失败，请刷新重试");
      } finally {
        setIsLoadingConfig(false);
      }
    }
    if (siteId) loadConfig();
  }, [siteId]);

  // 短信倒计时
  useEffect(() => {
    if (smsCountdown > 0) {
      const timer = setTimeout(() => setSmsCountdown(smsCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [smsCountdown]);

  // 发送短信验证码
  const handleSendSmsCode = useCallback(async () => {
    const phone = formDataRef.current['phone'] || '';
    if (!phone) { setError("请先输入手机号"); return; }
    if (!/^1[3-9]\d{9}$/.test(phone)) { setError("手机号格式不正确"); return; }
    try {
      setSmsLoading(true);
      await sendSmsCode(siteId, phone);
      setSmsCountdown(60);
      setError("");
    } catch (err: any) {
      setError(err.message || "短信发送失败，请重试");
    } finally {
      setSmsLoading(false);
    }
  }, [siteId]);

  // 打开协议弹窗
  const openAgreement = useCallback(async (slug: string, title: string) => {
    setAgreementModal({ show: true, title, content: "", loading: true });
    try {
      const page = await getSinglePage(slug);
      setAgreementModal({ show: true, title: page?.title || title, content: page?.content || "暂无内容", loading: false });
    } catch {
      setAgreementModal({ show: true, title, content: "加载失败，请稍后重试", loading: false });
    }
  }, []);

  const closeAgreement = useCallback(() => {
    setAgreementModal({ show: false, title: "", content: "", loading: false });
  }, []);

  // 处理输入变化
  const handleChange = useCallback((fieldName: string, value: string) => {
    setFormData((prev) => {
      const newData = { ...prev, [fieldName]: value };
      formDataRef.current = newData;
      return newData;
    });
  }, []);

  // 切换密码显示
  const togglePasswordVisibility = useCallback((fieldName: string) => {
    setShowPasswords((prev) => ({ ...prev, [fieldName]: !prev[fieldName] }));
  }, []);

  // 处理文件上传
  const handleFileUpload = useCallback(async (fieldName: string, file: File) => {
    try {
      setUploadingFields((prev) => ({ ...prev, [fieldName]: true }));
      const result = await uploadFile(file);
      handleChange(fieldName, result.url);
    } catch (err) {
      console.error("文件上传失败:", err);
      setError("文件上传失败，请重试");
    } finally {
      setUploadingFields((prev) => ({ ...prev, [fieldName]: false }));
    }
  }, [handleChange]);

  // 获取字段图标
  const getFieldIcon = (fieldType: FieldType) => {
    switch (fieldType) {
      case "text": case "number": return "ri-edit-line";
      case "textarea": return "ri-file-text-line";
      case "select": case "radio": case "checkbox": return "ri-list-check";
      case "password": return "ri-lock-line";
      case "date": return "ri-calendar-line";
      case "file": case "image": return "ri-image-add-line";
      default: return "ri-edit-line";
    }
  };

  // 渲染单个字段（排除 phone 和 sms_code，这些由短信区域处理）
  const renderField = (field: RegisterField) => {
    if (smsEnabled && (field.field_name === "phone" || field.field_name === "sms_code")) return null;

    const value = formDataRef.current[field.field_name] || "";
    const isPassword = field.field_type === "password";
    const showPassword = showPasswords[field.field_name] || false;
    const baseInputClass = `w-full pl-10 pr-4 py-3 bg-purple-50/20 border border-purple-100 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-purple-400 focus:ring-4 focus:ring-purple-500/10 transition-all`;

    switch (field.field_type) {
      case "textarea":
        return (
          <textarea
            value={value}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            placeholder={field.placeholder || field.hint || `请输入${field.field_label}`}
            rows={3}
            className="w-full px-4 py-3 bg-purple-50/20 border border-purple-100 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-purple-400 focus:ring-4 focus:ring-purple-500/10 transition-all resize-none"
          />
        );

      case "select":
        return (
          <select
            value={value}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            className={baseInputClass}
          >
            <option value="">{field.placeholder || `请选择${field.field_label}`}</option>
            {field.options?.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );

      case "radio":
        return (
          <div className="space-y-2">
            {field.options?.map((option) => (
              <label
                key={option}
                className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-colors ${
                  value === option ? "border-purple-200 bg-purple-50" : "border-purple-100 bg-purple-50/20"
                }`}
              >
                <input
                  type="radio"
                  name={field.field_name}
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleChange(field.field_name, e.target.value)}
                  className="w-4 h-4 accent-purple-500"
                />
                <span className="text-sm text-gray-700">{option}</span>
              </label>
            ))}
          </div>
        );

      case "checkbox":
        return (
          <div className="space-y-2">
            {field.options?.map((option) => (
              <label key={option} className="flex items-center gap-2 p-3 rounded-xl border border-purple-100 bg-purple-50/20 cursor-pointer">
                <input
                  type="checkbox"
                  name={field.field_name}
                  value={option}
                  checked={value.split(",").includes(option)}
                  onChange={(e) => {
                    const currentValues = value ? value.split(",") : [];
                    const newValues = e.target.checked ? [...currentValues, option] : currentValues.filter((v) => v !== option);
                    handleChange(field.field_name, newValues.join(","));
                  }}
                  className="w-4 h-4 accent-purple-500 rounded"
                />
                <span className="text-sm text-gray-700">{option}</span>
              </label>
            ))}
          </div>
        );

      case "date":
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            className={baseInputClass}
          />
        );

      case "number":
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            placeholder={field.placeholder || field.hint || `请输入${field.field_label}`}
            className={baseInputClass}
          />
        );

      case "file":
      case "image":
        return (
          <div>
            <label
              htmlFor={`file-${field.field_name}`}
              className="flex flex-col items-center justify-center w-full px-4 py-6 bg-purple-50/20 border-2 border-dashed border-purple-200 rounded-xl cursor-pointer hover:bg-purple-50 hover:border-purple-400 transition-colors"
            >
              {uploadingFields[field.field_name] ? (
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 border-4 border-purple-400 border-t-transparent rounded-full animate-spin mb-2" />
                  <span className="text-sm text-gray-500">上传中...</span>
                </div>
              ) : value ? (
                <div className="flex flex-col items-center">
                  {field.field_type === "image" ? (
                    <img src={value} alt="预览" className="w-24 h-24 object-cover rounded-lg mb-2" />
                  ) : (
                    <i className="ri-file-check-line text-3xl text-green-500 mb-2" />
                  )}
                  <span className="text-sm text-gray-600">点击重新上传</span>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <i className="ri-upload-cloud-2-line text-3xl text-purple-300 mb-2" />
                  <span className="text-sm text-gray-500">{field.placeholder || `点击上传${field.field_type === "image" ? "图片" : "文件"}`}</span>
                  {field.hint && <span className="text-xs text-gray-400 mt-1">{field.hint}</span>}
                </div>
              )}
            </label>
            <input
              id={`file-${field.field_name}`}
              type="file"
              accept={field.field_type === "image" ? "image/*" : "*/*"}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileUpload(field.field_name, file);
              }}
              className="hidden"
            />
            {value && (
              <button type="button" onClick={() => handleChange(field.field_name, "")} className="mt-2 text-sm text-red-500 hover:text-red-600">
                清除
              </button>
            )}
          </div>
        );

      default:
        return (
          <div className="relative">
            <input
              type={isPassword ? (showPassword ? "text" : "password") : "text"}
              value={value}
              onChange={(e) => handleChange(field.field_name, e.target.value)}
              placeholder={field.placeholder || field.hint || `请输入${field.field_label}`}
              className={isPassword ? `${baseInputClass} pr-11` : baseInputClass}
            />
            {isPassword && (
              <button
                type="button"
                onClick={() => togglePasswordVisibility(field.field_name)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className={showPassword ? "ri-eye-line" : "ri-eye-off-line"} />
              </button>
            )}
          </div>
        );
    }
  };

  // 根据表单填写进度计算步骤
  const getStep = () => {
    const currentData = formDataRef.current;
    const requiredFields = fields.filter(f => f.is_required);
    const filledRequired = requiredFields.filter(f => (currentData[f.field_name] || "").trim()).length;
    if (filledRequired >= requiredFields.length && agreed) return 3;
    if (filledRequired >= requiredFields.length) return 2;
    if (filledRequired > 0) return 1;
    return 0;
  };
  const currentStep = getStep();
  const stepTitles = ["账户信息", "完善资料", "确认注册"];

  // 表单验证
  const validate = () => {
    const currentData = formDataRef.current;
    if (!agreed) { setError("请先阅读并同意服务协议和隐私政策"); return false; }
    for (const field of fields) {
      if (field.is_required && !(currentData[field.field_name] || "").trim()) {
        setError(`${field.field_label}不能为空`);
        return false;
      }
    }
    if (smsEnabled) {
      const phone = currentData['phone'] || '';
      if (!phone) { setError("请输入手机号"); return false; }
      if (!/^1[3-9]\d{9}$/.test(phone)) { setError("手机号格式不正确"); return false; }
      const smsCode = currentData['sms_code'] || '';
      if (!smsCode) { setError("请输入短信验证码"); return false; }
      if (smsCode.length !== 6) { setError("短信验证码为6位数字"); return false; }
    }
    const usernameField = fields.find((f) => f.field_name === "username");
    if (usernameField && currentData.username) {
      if (!/^\w{3,20}$/.test(currentData.username)) { setError("用户名只能包含字母、数字和下划线，长度3-20位"); return false; }
    }
    if (currentData.password && currentData.password.length < 6) { setError("密码长度至少6位"); return false; }
    return true;
  };

  // 提交表单
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!validate()) return;

    setIsLoading(true);
    try {
      const currentData = formDataRef.current;
      const params: Record<string, string> = {
        username: currentData.username,
        password: currentData.password,
        site_id: siteId,
        confirm_password: currentData.confirm_password || '',
      };
      fields.forEach((field) => {
        if (!["username", "password", "nickname", "confirm_password"].includes(field.field_name)) {
          params[field.field_name] = currentData[field.field_name] ?? '';
        }
      });
      if (smsEnabled) {
        params.phone = currentData['phone'];
        params.sms_code = currentData['sms_code'];
      }

      await request("/auth/register", { method: "POST", body: JSON.stringify(params) });
      setSuccess("注册成功！即将跳转...");
      setTimeout(() => navigate("/"), 1500);
    } catch (err: any) {
      setError(err?.message || "注册失败，请重试");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingConfig) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-purple-500" />
          <p className="mt-2 text-gray-500 text-sm">加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white relative overflow-hidden p-4">
      {/* 背景装饰 */}
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-purple-300/10 blur-3xl" />
      <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-violet-300/10 blur-3xl" />

      <div className="w-full max-w-lg relative z-10">
        <div className="bg-white rounded-3xl shadow-2xl shadow-purple-500/10 border border-purple-100/50 p-8 sm:p-10">
          {/* Logo */}
          <div className="text-center mb-6">
            {logo ? (
              <img src={logo} alt={siteName} className="h-10 mx-auto" />
            ) : (
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-400 via-violet-500 to-fuchsia-500 mx-auto flex items-center justify-center text-white text-xl font-bold shadow-lg shadow-purple-500/20">
                {siteName ? siteName.charAt(0).toUpperCase() : 'P'}
              </div>
            )}
          </div>

          <h2 className="text-2xl font-bold text-gray-800 text-center mb-1">创建账户</h2>
          <p className="text-sm text-gray-400 text-center mb-8">注册加入{siteName || '我们'}</p>

          {/* 步骤进度指示器 */}
          <div className="flex items-center justify-center gap-0 mb-8">
            {stepTitles.map((title, idx) => (
              <div key={idx} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                      idx < currentStep
                        ? "bg-purple-500 text-white shadow-md shadow-purple-500/30"
                        : idx === currentStep
                        ? "bg-purple-100 text-purple-600 border-2 border-purple-400"
                        : "bg-gray-100 text-gray-400 border border-gray-200"
                    }`}
                  >
                    {idx < currentStep ? <i className="ri-check-line text-sm" /> : idx + 1}
                  </div>
                  <span className={`text-[10px] mt-1.5 font-medium ${idx <= currentStep ? 'text-purple-600' : 'text-gray-400'}`}>
                    {title}
                  </span>
                </div>
                {idx < stepTitles.length - 1 && (
                  <div className={`w-10 sm:w-16 h-0.5 mx-1 mb-5 transition-all ${idx < currentStep ? 'bg-purple-400' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>

          {error && (
            <div className="mb-5 p-3.5 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600 flex items-center gap-2">
              <i className="ri-error-warning-line" />{error}
            </div>
          )}

          {success && (
            <div className="mb-5 p-3.5 bg-green-50 border border-green-100 rounded-xl text-sm text-green-600 flex items-center gap-2">
              <i className="ri-check-line" />{success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* 动态渲染表单字段 */}
            {fields.map((field) => {
              const smsFields = smsEnabled && (field.field_name === "phone" || field.field_name === "sms_code");
              if (smsFields) return null;
              return (
                <div key={field.field_name}>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1">
                    {field.field_label}
                    {field.is_required && <span className="text-red-500 ml-0.5">*</span>}
                  </label>
                  <div className="relative">
                    <i className={`${getFieldIcon(field.field_type)} absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400`} />
                    {renderField(field)}
                  </div>
                  {field.hint && <p className="mt-1 text-[11px] text-gray-400">{field.hint}</p>}
                </div>
              );
            })}

            {/* 短信验证（如果启用） */}
            {smsEnabled && (
              <>
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1">手机号 <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <i className="ri-phone-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="tel"
                      value={formData['phone'] || ''}
                      onChange={(e) => { handleChange('phone', e.target.value); setError(""); }}
                      placeholder="请输入手机号"
                      className="w-full pl-10 pr-24 py-3 bg-purple-50/20 border border-purple-100 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-purple-400 focus:ring-4 focus:ring-purple-500/10 transition-all"
                    />
                    <button
                      type="button"
                      onClick={handleSendSmsCode}
                      disabled={smsCountdown > 0 || smsLoading}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 px-3 py-1.5 text-xs bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:bg-gray-300 disabled:text-gray-500 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {smsLoading ? '发送中...' : smsCountdown > 0 ? `${smsCountdown}s后重发` : '获取验证码'}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1">短信验证码 <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <i className="ri-shield-check-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={formData['sms_code'] || ''}
                      onChange={(e) => { handleChange('sms_code', e.target.value.replace(/\D/g, '').slice(0, 6)); setError(""); }}
                      placeholder="请输入6位验证码"
                      maxLength={6}
                      className="w-full pl-10 pr-4 py-3 bg-purple-50/20 border border-purple-100 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-purple-400 focus:ring-4 focus:ring-purple-500/10 transition-all tracking-widest"
                    />
                  </div>
                </div>
              </>
            )}

            {/* 协议 */}
            <div className="pt-1">
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="w-4 h-4 rounded accent-purple-500 mt-0.5 shrink-0"
                />
                <span className="text-xs text-gray-500 leading-relaxed">
                  我已阅读并同意
                  <span className="text-purple-600 hover:text-purple-700 mx-1 cursor-pointer" onClick={() => openAgreement("register-agreement", "注册协议")}>《注册协议》</span>
                  和
                  <span className="text-purple-600 hover:text-purple-700 mx-1 cursor-pointer" onClick={() => openAgreement("privacy", "隐私政策")}>《隐私政策》</span>
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 bg-gradient-to-r from-purple-600 via-violet-600 to-fuchsia-600 text-white font-bold rounded-2xl hover:from-purple-700 hover:via-violet-700 hover:to-fuchsia-700 transition-all shadow-lg shadow-purple-500/25 disabled:opacity-60 disabled:cursor-not-allowed text-sm mt-2"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin" />注册中...
                </span>
              ) : "创建账户"}
            </button>
          </form>

          <div className="mt-6 flex items-center justify-between text-sm">
            <Link to="/" className="text-gray-400 hover:text-gray-500 inline-flex items-center gap-1">
              <i className="ri-arrow-left-line" />返回首页
            </Link>
            <p className="text-gray-400">
              已有账号？{" "}
              <Link to="/login" className="text-purple-600 hover:text-purple-700 font-semibold">登录</Link>
            </p>
          </div>
        </div>
      </div>

      {/* 协议弹窗 */}
      {agreementModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={closeAgreement}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <i className="ri-file-text-line text-purple-500" />{agreementModal.title}
              </h2>
              <button onClick={closeAgreement} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {agreementModal.loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="text-center">
                    <i className="ri-loader-4-line animate-spin text-2xl text-purple-400" />
                    <p className="mt-2 text-sm text-gray-400">加载中...</p>
                  </div>
                </div>
              ) : (
                <div className="prose prose-sm max-w-none text-gray-600 leading-relaxed whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: agreementModal.content }} />
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 shrink-0 flex justify-end">
              <button onClick={closeAgreement} className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-violet-600 text-white font-medium rounded-xl hover:from-purple-700 hover:to-violet-700 transition-all text-sm cursor-pointer">
                已了解，关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
